import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/routes/ProtectedRoute";
import { AppShell } from "@/components/AppShell";

import { LoginPage } from "@/pages/LoginPage";
import { ChangePasswordPage } from "@/pages/ChangePasswordPage";
import { MerchantsListPage } from "@/pages/MerchantsListPage";
import { MerchantDetailPage } from "@/pages/MerchantDetailPage";
import { PaymentsPage } from "@/pages/PaymentsPage";
import { PaymentDetailPage } from "@/pages/PaymentDetailPage";
import { MerchantOverviewPage } from "@/pages/MerchantOverviewPage";
import { MerchantSettingsPage } from "@/pages/MerchantSettingsPage";
import { MerchantTeamPage } from "@/pages/MerchantTeamPage";
import { MerchantApiKeysPage } from "@/pages/MerchantApiKeysPage";
import { StatementsPage } from "@/pages/StatementsPage";
import { HistoryPage } from "@/pages/HistoryPage";
import { SettlementsPage } from "@/pages/SettlementsPage";
import { DailyBalancesPage } from "@/pages/DailyBalancesPage";
import { CardRefundBatchesPage } from "@/pages/CardRefundBatchesPage";
import { PendingRefundsPage } from "@/pages/PendingRefundsPage";
import { TransactionStatusesPage } from "@/pages/TransactionStatusesPage";
import { CallbackResendPage } from "@/pages/CallbackResendPage";
import { UserRolesPage } from "@/pages/UserRolesPage";
import { FunnelPage } from "@/pages/FunnelPage";
import { WalletsPage } from "@/pages/WalletsPage";
import { SwitchLogsPage } from "@/pages/SwitchLogsPage";
import { WebhooksPage } from "@/pages/WebhooksPage";
import { AuditLogPage } from "@/pages/AuditLogPage";
import { AdminMokoTeamPage } from "@/pages/AdminMokoTeamPage";
import { HelpPage } from "@/pages/HelpPage";
import { HelpRefundsPage } from "@/pages/HelpRefundsPage";
import { NotificationsPage } from "@/pages/NotificationsPage";
import { SupportListPage } from "@/pages/SupportListPage";
import { SupportDetailPage } from "@/pages/SupportDetailPage";
import { UpdateBanner } from "@/components/UpdateBanner";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

function Shell({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: readonly any[] }) {
  return (
    <ProtectedRoute allowedRoles={allowedRoles as any}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

function NotFoundPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="text-5xl font-bold text-brand-700 mb-2">404</div>
        <div className="text-slate-600">Page non trouvée</div>
      </div>
    </div>
  );
}

function ForbiddenPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="text-center max-w-md">
        <div className="text-5xl font-bold text-red-700 mb-2">403</div>
        <div className="text-slate-700 font-medium mb-1">Accès refusé</div>
        <div className="text-sm text-slate-500">Votre rôle n'a pas la permission d'accéder à cette page.</div>
      </div>
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <UpdateBanner />
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forbidden" element={<ForbiddenPage />} />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />

            {/* Admin */}
            <Route
              path="/admin/merchants"
              element={<Shell allowedRoles={["admin"] as const}><MerchantsListPage /></Shell>}
            />
            <Route
              path="/admin/merchants/new"
              element={<Navigate to="/admin/merchants" replace />}
            />
            <Route
              path="/admin/merchants/:id"
              element={<Shell allowedRoles={["admin"] as const}><MerchantDetailPage /></Shell>}
            />
            <Route
              path="/admin/audit"
              element={<Shell allowedRoles={["admin"] as const}><AuditLogPage /></Shell>}
            />
            <Route
              path="/admin/moko-team"
              element={<Shell allowedRoles={["admin"] as const}><AdminMokoTeamPage /></Shell>}
            />

            {/* Commercial / Admin / Merchant */}
            <Route
              path="/payments"
              element={<Shell allowedRoles={["commercial", "finance", "it", "admin", "merchant_admin"] as const}><PaymentsPage /></Shell>}
            />
            <Route
              path="/payments/:id"
              element={<Shell allowedRoles={["commercial", "finance", "it", "admin", "merchant_admin"] as const}><PaymentDetailPage /></Shell>}
            />
            <Route
              path="/funnel"
              element={<Shell allowedRoles={["commercial", "finance", "it", "admin", "merchant_admin"] as const}><FunnelPage /></Shell>}
            />

            {/* Finance */}
            <Route
              path="/wallets"
              element={<Shell allowedRoles={["finance", "admin", "merchant_admin"] as const}><WalletsPage /></Shell>}
            />

            {/* IT — Switch logs reserve a Moko Afrika staff (admin) seulement.
               Webhooks ouvert au role `it` du marchand (debug integration). */}
            <Route
              path="/it/switch-logs"
              element={<Shell allowedRoles={["admin"] as const}><SwitchLogsPage /></Shell>}
            />
            <Route
              path="/it/webhooks"
              element={<Shell allowedRoles={["it", "admin"] as const}><WebhooksPage /></Shell>}
            />

            {/* Merchant portal (self-service) */}
            <Route
              path="/merchant/overview"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial"] as const}><MerchantOverviewPage /></Shell>}
            />
            <Route
              path="/merchant/settings"
              element={<Shell allowedRoles={["merchant_admin"] as const}><MerchantSettingsPage /></Shell>}
            />
            <Route
              path="/merchant/team"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial"] as const}><MerchantTeamPage /></Shell>}
            />
            <Route
              path="/merchant/api-keys"
              element={<Shell allowedRoles={["it", "admin"] as const}><MerchantApiKeysPage /></Shell>}
            />

            {/* Finance */}
            <Route
              path="/finance/statements"
              element={<Shell allowedRoles={["merchant_admin", "finance", "admin"] as const}><StatementsPage /></Shell>}
            />
            <Route
              path="/finance/history"
              element={<Shell allowedRoles={["merchant_admin", "finance", "admin"] as const}><HistoryPage /></Shell>}
            />
            <Route
              path="/finance/settlements"
              element={<Shell allowedRoles={["merchant_admin", "finance", "admin"] as const}><SettlementsPage /></Shell>}
            />
            <Route
              path="/finance/daily-balances"
              element={<Shell allowedRoles={["merchant_admin", "finance", "admin"] as const}><DailyBalancesPage /></Shell>}
            />
            <Route
              path="/finance/card-refund-batches"
              element={<Shell allowedRoles={["finance", "admin"] as const}><CardRefundBatchesPage /></Shell>}
            />
            {/* Refunds — double validation (spec CAA #6). Lecture ouverte à tous
                les rôles tenant ; les actions valider/rejeter sont gardées côté
                composant + backend (finance, merchant_admin, admin). */}
            <Route
              path="/refunds/pending"
              element={<Shell allowedRoles={["commercial", "finance", "merchant_admin", "it", "admin"] as const}><PendingRefundsPage /></Shell>}
            />

            {/* Transactions */}
            <Route
              path="/transactions/statuses"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial", "admin"] as const}><TransactionStatusesPage /></Shell>}
            />
            <Route
              path="/transactions/callback-resend"
              element={<Shell allowedRoles={["merchant_admin", "it", "admin"] as const}><CallbackResendPage /></Shell>}
            />

            {/* Roles doc */}
            <Route
              path="/merchant/roles"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial"] as const}><UserRolesPage /></Shell>}
            />

            {/* Help center — accessible a tous les roles authentifies */}
            <Route
              path="/help"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial", "admin"] as const}><HelpPage /></Shell>}
            />
            <Route
              path="/help/refunds"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial", "admin"] as const}><HelpRefundsPage /></Shell>}
            />

            {/* Notifications — accessible a tous les roles authentifies */}
            <Route
              path="/notifications"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial", "admin"] as const}><NotificationsPage /></Shell>}
            />

            {/* Support tickets — accessible a tous les roles authentifies */}
            <Route
              path="/support"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial", "admin"] as const}><SupportListPage /></Shell>}
            />
            <Route
              path="/support/:ticketId"
              element={<Shell allowedRoles={["merchant_admin", "finance", "it", "commercial", "admin"] as const}><SupportDetailPage /></Shell>}
            />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
